import type { ReactNode } from "react";
import { Link } from "react-router";
import { PublicPageLayout } from "@/components/PublicPageLayout";

interface DataCategory {
  title: string;
  description: string;
  purpose: string;
}

interface RetentionRow {
  data: string;
  period: string;
}

const TEXT_LINK =
  "font-medium text-[var(--cs-navy)] underline decoration-[rgba(12,63,107,0.25)] underline-offset-4 hover:decoration-[rgba(12,63,107,0.55)]";

const DATA_CATEGORIES: DataCategory[] = [
  {
    title: "Account en toegang",
    description:
      "Naam, e-mailadres, organisatie, rol, instellingen, authenticatie-, apparaat- en beveiligingsgegevens.",
    purpose:
      "Om accounts aan te maken, toegang te beveiligen, voorkeuren te synchroniseren en de dienst te ondersteunen.",
  },
  {
    title: "Medische inhoud",
    description:
      "Audio, dictaten, consultgesprekken, transcripties, bestaande verslagtekst, patiëntreferenties en gegenereerde teksten of taken.",
    purpose:
      "Voor transcriptie, structurering, vertaling, verslagopbouw en andere functies die de gebruiker of klant activeert.",
  },
  {
    title: "Gebruik en technische werking",
    description:
      "Gebruikers- en sessie-ID's, IP-adres, gebruikte functie, duur, succes- of foutstatus, modelgebruik en technische logcontext.",
    purpose:
      "Voor beveiliging, foutopsporing, capaciteitsbeheer, facturatie en verbetering van de betrouwbaarheid.",
  },
  {
    title: "Betaling en administratie",
    description:
      "Plan, facturatiegegevens, klant- en abonnements-ID en betalingsstatus. Kaartgegevens worden in de gehoste Stripe-omgeving verwerkt.",
    purpose: "Voor abonnementen, betalingen, fraudepreventie en wettelijke boekhouding.",
  },
  {
    title: "Support en website",
    description:
      "Support- of feedbackberichten en beperkte bezoek- en apparaatgegevens van de publieke website.",
    purpose:
      "Om vragen te beantwoorden, problemen te onderzoeken en het gebruik van de website te begrijpen. We gebruiken deze gegevens niet voor advertentieprofilering.",
  },
];

const RETENTION_ROWS: RetentionRow[] = [
  {
    data: "Opnamegeschiedenis",
    period:
      "30 tot 365 dagen, standaard 365 dagen. Verwijderde opnames blijven maximaal 30 dagen in de prullenbak.",
  },
  {
    data: "Live sessies en klinische workflows",
    period:
      "Zolang nodig voor de workflow, klantinstructie of klantrelatie. Niet elke workflowtabel valt onder de bewaartermijn van opnamegeschiedenis.",
  },
  {
    data: "Lokale herstel- en synchronisatiekopieën",
    period:
      "Tot synchronisatie, cachevervanging of verwijdering op het apparaat. Mislukte webuploads worden maximaal zeven dagen bewaard.",
  },
  {
    data: "Logs, gebruik en websiteanalyse",
    period:
      "Serverlogs 30 dagen, e-mailverzendlog 90 dagen, gebruikslog 365 dagen, websiteanalyse 12 maanden en geïdentificeerde productevents 24 maanden. Voor auditgegevens geldt nog geen uniforme automatische maximumtermijn.",
  },
  {
    data: "Feedback, taken en support",
    period:
      "Algemene appfeedback 12 maanden; overige feedback, taken en support zolang nodig voor opvolging of volgens instructie van de klant.",
  },
  {
    data: "Account en boekhouding",
    period:
      "Accountgegevens tijdens de klantrelatie; boekhoudstukken 10 jaar volgens de Belgische bewaarplicht.",
  },
];

function PolicySection({
  number,
  title,
  children,
  id,
}: {
  number: string;
  title: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-[#DCE4EA] py-9 sm:py-11">
      <div className="grid gap-4 sm:grid-cols-[44px_minmax(0,1fr)] sm:gap-5">
        <span className="text-[13px] font-medium tabular-nums text-[#8291A3]">{number}</span>
        <div>
          <h2 className="text-[24px] font-semibold leading-tight tracking-[-0.02em] text-[var(--cs-navy)]">
            {title}
          </h2>
          <div className="mt-5 space-y-4 text-[15px] leading-7 text-[#536274]">{children}</div>
        </div>
      </div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <PublicPageLayout>
      <article className="mx-auto w-full max-w-[900px] text-[var(--cs-navy)]">
        <header className="pb-10 sm:pb-14">
          <h1 className="text-[42px] font-semibold leading-[1.04] tracking-[-0.04em] sm:text-[58px]">
            Privacybeleid
          </h1>
          <p className="mt-4 text-[13px] text-[#6E7D8F]">
            <time dateTime="2026-10-04">4 oktober 2026</time>
          </p>
          <p className="mt-8 max-w-[800px] text-[17px] leading-8 text-[#536274]">
            Dit privacybeleid beschrijft hoe Carmen Labs BV (“Carmen”, “wij”
            of “ons”) persoonsgegevens verzamelt, gebruikt en verwerkt, en welke keuzes en
            rechten u hebt wanneer u onze website, applicaties, platformen en diensten (samen
            de “Diensten”) gebruikt. Door de Diensten te gebruiken, bevestigt u dat u dit
            privacybeleid hebt gelezen. Als u niet akkoord gaat met de beschreven verwerking,
            gebruik de Diensten dan niet. Begrippen met een hoofdletter die hier niet zijn
            gedefinieerd, hebben de betekenis uit onze{" "}
            <Link to="/terms" className={TEXT_LINK}>
              Gebruiksvoorwaarden
            </Link>
            .
          </p>
        </header>

        <PolicySection number="01" title="Wie is verantwoordelijk?">
          <p>
            <strong className="font-semibold text-[var(--cs-navy)]">
              Carmen Labs BV
            </strong>
            , gevestigd in Gent, België (ondernemingsnummer BE 1043.364.959), biedt Carmen aan. Carmen heeft vrijwillig een
            functionaris voor gegevensbescherming (DPO) aangesteld. Voor privacyvragen of
            contact met de DPO mailt u naar{" "}
            <a href="mailto:info@askcarmen.eu" className={TEXT_LINK}>
              info@askcarmen.eu
            </a>
            .
          </p>
          <p>
            Voor medische gegevens bepaalt de zelfstandige arts, praktijk, het ziekenhuis of
            de andere zorgorganisatie waarom Carmen wordt gebruikt. Die partij is doorgaans
            verwerkingsverantwoordelijke; Carmen verwerkt de gegevens in haar opdracht. Voor
            eigen accountbeheer, beveiliging, facturatie, support en websiteanalyse is Carmen
            zelf verwerkingsverantwoordelijke.
          </p>
          <p>
            Gegevens komen van de gebruiker of klant en uit de gekozen zorgworkflow.
          </p>
        </PolicySection>

        <PolicySection number="02" title="Welke gegevens verwerken we en waarom?">
          <div className="border-y border-[#DCE4EA]">
            {DATA_CATEGORIES.map((category) => (
              <div
                key={category.title}
                className="grid gap-2 border-b border-[#E8EDF1] py-5 last:border-b-0 md:grid-cols-[190px_minmax(0,1fr)] md:gap-7"
              >
                <h3 className="text-[15px] font-semibold leading-6 text-[var(--cs-navy)]">
                  {category.title}
                </h3>
                <div className="space-y-1 text-[14px] leading-6">
                  <p>{category.description}</p>
                  <p className="text-[#738093]">{category.purpose}</p>
                </div>
              </div>
            ))}
          </div>
          <p>
            Voor persoonsgegevens waarvoor Carmen zelf verantwoordelijk is, verwerken we
            gegevens wanneer dat nodig is om onze overeenkomst uit te voeren, aan wettelijke
            verplichtingen te voldoen of onze dienst veilig en betrouwbaar te houden. In dat
            laatste geval steunen we op ons gerechtvaardigd belang. De zelfstandige arts,
            praktijk of zorgorganisatie bepaalt de grondslag voor medische gegevens, inclusief
            de toepasselijke uitzondering voor gezondheidsgegevens onder artikel 9 AVG.
          </p>
        </PolicySection>

        <PolicySection number="03" title="AI, apparaten en menselijke controle">
          <p>
            Voor transcriptie en tekstgeneratie kan Carmen de nodige audio, tekst en gekozen
            context verwerken met eigen AI-modellen of met modellen van OpenAI. Zowel bij onze
            eigen modellen als bij OpenAI wordt klantinhoud via Europese infrastructuur
            verwerkt. Voor OpenAI geldt een Zero Data Retention-overeenkomst:
            OpenAI bewaart de klantinhoud niet na de verwerking en neemt ze ook niet op in de
            standaardlogs die anders maximaal 30 dagen voor misbruikmonitoring kunnen worden
            bewaard. OpenAI gebruikt deze klantinhoud niet voor modeltraining en Carmen traint
            er geen eigen AI-model mee. OpenAI kan wel afzonderlijk systeem- en
            gebruiksmetadata verwerken. De officiële documentatie beschrijft de voorwaarden
            voor{" "}
            <a
              href="https://developers.openai.com/api/docs/guides/your-data#zero-data-retention"
              target="_blank"
              rel="noreferrer"
              className={TEXT_LINK}
            >
              Zero Data Retention
            </a>
            {" "}en{" "}
            <a
              href="https://developers.openai.com/api/docs/guides/your-data#data-residency-controls"
              target="_blank"
              rel="noreferrer"
              className={TEXT_LINK}
            >
              Europese dataresidentie
            </a>
            .
          </p>
          <p>
            De desktopapp gebruikt microfoon-, sneltoets- en toegankelijkheidsrechten om een
            opname te bedienen en tekst bij de cursor te plaatsen. Voor opmaak kan lokaal een
            beperkte venstercontext worden bekeken.
          </p>
          <p>
            Carmen is een documentatiehulpmiddel en stelt geen diagnose. Het genereert
            concepttekst, coderingen en suggesties die een bevoegde zorgverlener moet
            controleren. Carmen neemt geen uitsluitend geautomatiseerde medische beslissing
            met rechtsgevolg voor een patiënt.
          </p>
        </PolicySection>

        <PolicySection number="04" title="Bewaartermijnen" id="retention">
          <p>
            Hoe lang we gegevens bewaren, hangt af van het type gegevens en de gekozen
            instellingen. Hieronder vindt u de toepasselijke termijnen.
          </p>
          <dl className="border-y border-[#DCE4EA]">
            {RETENTION_ROWS.map((row) => (
              <div
                key={row.data}
                className="grid gap-1 border-b border-[#E8EDF1] py-4 last:border-b-0 md:grid-cols-[210px_minmax(0,1fr)] md:gap-7"
              >
                <dt className="text-[14px] font-semibold leading-6 text-[var(--cs-navy)]">
                  {row.data}
                </dt>
                <dd className="text-[14px] leading-6 text-[#647286]">{row.period}</dd>
              </div>
            ))}
          </dl>
          <p>
            U kunt opnames en de bijbehorende gegevens afzonderlijk verwijderen. Uw volledige
            account kunt u verwijderen via de accountinstellingen of door te mailen naar{" "}
            <a href="mailto:info@askcarmen.eu" className={TEXT_LINK}>
              info@askcarmen.eu
            </a>
            . We voeren de verwijdering zonder onnodige vertraging uit. Gegevens die we
            wettelijk moeten bewaren, houden we alleen bij gedurende de verplichte termijn.
            Een volledige accountverwijdering is definitief.
          </p>
        </PolicySection>

        <PolicySection number="05" title="Ontvangers en doorgiften" id="sub-processors">
          <p>
            Binnen een praktijk of zorgorganisatie is toegang beperkt volgens rollen en
            afdelingen. Voor de technische dienstverlening gebruiken we onder meer OpenAI,
            Hetzner, Stripe, Resend, GitLab en Expo. Het actuele overzicht en hun
            functie vindt u in het{" "}
            <Link to="/trust#sub-processors" className={TEXT_LINK}>
              Trust Center
            </Link>
            . We verkopen geen persoonsgegevens.
          </p>
          <p>
            Primaire applicatiehosting, database en opnameopslag zijn in de EU ingericht.
            Sommige leveranciers zijn buiten de EER gevestigd. Waar een doorgifte buiten de
            EER plaatsvindt, steunen we op een adequaatheidsbesluit van de Europese Commissie,
            zoals het EU-VS Data Privacy Framework voor e-mailverzending via Resend. Een kopie
            van de toepasselijke waarborg is opvraagbaar.
          </p>
          <p>
            Resend wordt gebruikt voor transactionele e-mails en verwerkt daarbij het
            e-mailadres van de ontvanger, berichtmetadata en de inhoud van het bericht.
          </p>
        </PolicySection>

        <PolicySection number="06" title="Beveiliging">
          <p>
            We gebruiken onder meer versleutelde verbindingen met TLS 1.2 of hoger,
            authenticatie, toegangsbeheer, beveiligingslogging en periodieke
            beveiligingsbeoordelingen. Gearchiveerde audio
            en de daaraan gekoppelde transcripties en resultaten worden met
            AES-256-versleuteling opgeslagen. Live
            sessies, workflowtabellen en lokale caches gebruiken niet allemaal dezelfde extra
            applicatieversleuteling en worden beschermd door de toegangs- en
            infrastructuurmaatregelen van die opslag.
          </p>
          <p>
            Operationele logs kunnen in sommige foutpaden een patiëntreferentie of klinische
            context bevatten. Daarom behandelen we ze als persoonsgegevens, beperken we de
            toegang en bewaren we serverlogs 30 dagen. Meer informatie en
            beoordelingsdocumentatie staat in het{" "}
            <Link to="/trust" className={TEXT_LINK}>
              Trust Center
            </Link>
            .
          </p>
        </PolicySection>

        <PolicySection number="07" title="Uw rechten" id="rights">
          <p>
            Afhankelijk van de verwerking kunt u vragen om inzage, correctie, verwijdering,
            beperking of overdracht van uw gegevens. U kunt bezwaar maken tegen verwerking
            op basis van gerechtvaardigd belang en toestemming intrekken wanneer toestemming
            de grondslag is.
          </p>
          <p>
            Voor medische gegevens neemt u bij voorkeur contact op met de arts, praktijk of
            zorgorganisatie die Carmen gebruikte; wij helpen die partij het verzoek uit te
            voeren. Voor gegevens waarvoor Carmen zelf verantwoordelijk is, mailt u naar{" "}
            <a href="mailto:info@askcarmen.eu" className={TEXT_LINK}>
              info@askcarmen.eu
            </a>
            . We kunnen informatie vragen om uw identiteit te controleren en antwoorden in
            beginsel binnen één maand.
          </p>
          <p>
            U hebt ook het recht een klacht in te dienen bij de bevoegde toezichthoudende
            autoriteit.
          </p>
        </PolicySection>

        <PolicySection number="08" title="Praktische informatie">
          <p>
            Een account vereist een e-mailadres en authenticatiegegevens. Audio, tekst en een
            patiëntreferentie zijn alleen verplicht wanneer de gekozen functie die nodig
            heeft. Zonder de noodzakelijke gegevens kan die functie niet werken. Carmen-
            accounts zijn bedoeld voor professionele gebruikers; medische gegevens kunnen
            betrekking hebben op patiënten van elke leeftijd.
          </p>
          <p>
            We actualiseren dit beleid wanneer de dienst, wetgeving of gegevensstromen
            wezenlijk veranderen. Bij een belangrijke wijziging informeren we gebruikers of
            klanten via een passend kanaal wanneer dat vereist is.
          </p>
          <p className="pt-2 text-[14px] text-[#6E7D8F]">
            Carmen Labs BV · BE 1043.364.959 · Gent, België ·{" "}
            <a href="mailto:info@askcarmen.eu" className={TEXT_LINK}>
              info@askcarmen.eu
            </a>
          </p>
        </PolicySection>
      </article>
    </PublicPageLayout>
  );
}
